import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Users, Mail, Send, UserPlus, ArrowLeft, Loader2, Pencil, Check, ArrowUp, ArrowDown, CreditCard, Calendar, DollarSign, Sparkles, Building2, LogIn, Info, Lock, RefreshCw } from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const AdminCRM = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadingStats, setLoadingStats] = useState(true);
  const [sendingDigestFor, setSendingDigestFor] = useState(null);
  const [sendingBriefDigestFor, setSendingBriefDigestFor] = useState(null);
  const [togglingSubscriptionFor, setTogglingSubscriptionFor] = useState(null);
  const [addName, setAddName] = useState('');
  const [addEmail, setAddEmail] = useState('');
  const [adding, setAdding] = useState(false);
  const [addAndSend, setAddAndSend] = useState(false);
  const [editingNameFor, setEditingNameFor] = useState(null);
  const [editingNameValue, setEditingNameValue] = useState('');
  const [savingNameFor, setSavingNameFor] = useState(null);
  const [editingEmailFor, setEditingEmailFor] = useState(null);
  const [editingEmailValue, setEditingEmailValue] = useState('');
  const [savingEmailFor, setSavingEmailFor] = useState(null);
  const [sortBy, setSortBy] = useState('created_at');
  const [sortDir, setSortDir] = useState('desc');
  const [digestPeriods, setDigestPeriods] = useState([]);
  const [selectedDigestPeriod, setSelectedDigestPeriod] = useState(null);
  const [digestMetrics, setDigestMetrics] = useState(null);
  const [loadingDigestMetrics, setLoadingDigestMetrics] = useState(false);
  const [trendProWaitlist, setTrendProWaitlist] = useState([]);
  const [trendProWaitlistTotal, setTrendProWaitlistTotal] = useState(0);
  const [trendProWaitlistReturned, setTrendProWaitlistReturned] = useState(0);
  const [trendProWaitlistNote, setTrendProWaitlistNote] = useState(null);
  const [loadingTrendProWaitlist, setLoadingTrendProWaitlist] = useState(true);
  const [refreshingTrendProWaitlist, setRefreshingTrendProWaitlist] = useState(false);

  const fetchTrendProWaitlist = useCallback(async (isRefresh) => {
    if (isRefresh) setRefreshingTrendProWaitlist(true);
    else setLoadingTrendProWaitlist(true);
    try {
      const res = await axios.get('/api/admin/crm/trend-pro-chat-waitlist', { withCredentials: true });
      if (res.data.success) {
        setTrendProWaitlist(res.data.entries || []);
        setTrendProWaitlistTotal(typeof res.data.total === 'number' ? res.data.total : (res.data.entries || []).length);
        setTrendProWaitlistReturned(
          typeof res.data.returned === 'number' ? res.data.returned : (res.data.entries || []).length
        );
        setTrendProWaitlistNote(res.data.note || null);
      } else {
        setTrendProWaitlist([]);
        setTrendProWaitlistNote(res.data.error || null);
      }
    } catch {
      setTrendProWaitlist([]);
      setTrendProWaitlistNote('Failed to load waitlist');
    } finally {
      setLoadingTrendProWaitlist(false);
      setRefreshingTrendProWaitlist(false);
    }
  }, []);

  useEffect(() => {
    if (user && user.email && user.email.toLowerCase() !== 'info@showstop.ai') {
      toast.error('Access denied. Admin only.');
      navigate('/trend-spotter');
      return;
    }
    fetchUsers();
    fetchStats();
    fetchTrendProWaitlist(false);
  }, [user, navigate, fetchTrendProWaitlist]);

  useEffect(() => {
    if (!user || user.email?.toLowerCase() !== 'info@showstop.ai') return;
    let cancelled = false;
    (async () => {
      try {
        const res = await axios.get('/api/admin/crm/weekly-digest-periods', { withCredentials: true });
        if (cancelled || !res.data.success) return;
        const periods = res.data.periods || [];
        setDigestPeriods(periods);
        if (periods.length) setSelectedDigestPeriod(periods[0]);
      } catch {
        /* optional */
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [user]);

  useEffect(() => {
    if (!selectedDigestPeriod) return;
    let cancelled = false;
    (async () => {
      setLoadingDigestMetrics(true);
      try {
        const res = await axios.get('/api/admin/crm/weekly-digest-metrics', {
          params: {
            periodStart: selectedDigestPeriod.periodStart,
            periodEnd: selectedDigestPeriod.periodEnd
          },
          withCredentials: true
        });
        if (!cancelled && res.data.success) setDigestMetrics(res.data);
      } catch {
        if (!cancelled) setDigestMetrics(null);
      } finally {
        if (!cancelled) setLoadingDigestMetrics(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [selectedDigestPeriod]);

  const fetchUsers = async () => {
    try {
      setLoading(true);
      const res = await axios.get('/api/admin/crm/users', { withCredentials: true });
      if (res.data.success) {
        setUsers(res.data.users || []);
      } else {
        toast.error(res.data.error || 'Failed to load users');
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Access denied. Admin only.');
        navigate('/trend-spotter');
      } else {
        toast.error('Failed to load users');
      }
    } finally {
      setLoading(false);
    }
  };

  const fetchStats = async () => {
    try {
      setLoadingStats(true);
      const res = await axios.get('/api/admin/crm/stats', { withCredentials: true });
      if (res.data.success) {
        setStats(res.data);
      }
    } catch (e) {
      if (e.response?.status === 403) return;
      console.error('Failed to load CRM stats', e);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleToggleSubscription = async (userId, currentSubscribed) => {
    const newSubscribed = !currentSubscribed;
    try {
      setTogglingSubscriptionFor(userId);
      const res = await axios.patch(
        `/api/admin/crm/users/${userId}/subscription`,
        { subscribed: newSubscribed },
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(newSubscribed ? 'Subscribed to weekly digest (Sundays 12p PT)' : 'Unsubscribed from weekly digest');
        fetchUsers();
      } else {
        toast.error(res.data.error || 'Failed to update subscription');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update subscription');
    } finally {
      setTogglingSubscriptionFor(null);
    }
  };

  const refetchDigestMetrics = async () => {
    if (!selectedDigestPeriod) return;
    try {
      const res = await axios.get('/api/admin/crm/weekly-digest-metrics', {
        params: {
          periodStart: selectedDigestPeriod.periodStart,
          periodEnd: selectedDigestPeriod.periodEnd
        },
        withCredentials: true
      });
      if (res.data.success) setDigestMetrics(res.data);
      const pr = await axios.get('/api/admin/crm/weekly-digest-periods', { withCredentials: true });
      if (pr.data.success) setDigestPeriods(pr.data.periods || []);
    } catch {
      /* ignore */
    }
  };

  const handleSendDigest = async (email) => {
    try {
      setSendingDigestFor(email);
      const res = await axios.post('/api/admin/crm/send-digest', { email }, { withCredentials: true });
      if (res.data.success) {
        toast.success(`Digest sent to ${email}`);
        await refetchDigestMetrics();
      } else {
        toast.error(res.data.error || 'Failed to send digest');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to send digest');
    } finally {
      setSendingDigestFor(null);
    }
  };

  const handleSendBriefDigest = async (email, eligible) => {
    if (!eligible) return;
    try {
      setSendingBriefDigestFor(email);
      const res = await axios.post('/api/admin/crm/send-brief-digest', { email }, { withCredentials: true });
      if (res.data.success) {
        toast.success(`Friday brief digest sent to ${email}`);
      } else {
        toast.error(res.data.error || 'Failed to send Friday brief');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.message || 'Failed to send Friday brief');
    } finally {
      setSendingBriefDigestFor(null);
    }
  };

  const handleAddUser = async (sendDigest) => {
    const email = addEmail.trim();
    if (!email) {
      toast.error('Email is required');
      return;
    }
    try {
      setAdding(true);
      const res = await axios.post(
        '/api/admin/crm/users',
        { name: addName.trim() || undefined, email, sendDigest },
        { withCredentials: true }
      );
      if (res.data.success) {
        toast.success(res.data.digestSent ? 'User added and digest sent' : 'User added');
        setAddName('');
        setAddEmail('');
        fetchUsers();
        if (res.data.digestSent) await refetchDigestMetrics();
      } else {
        toast.error(res.data.error || 'Failed to add user');
      }
    } catch (e) {
      if (e.response?.status === 403) {
        toast.error('Access denied. Admin only.');
      } else {
        toast.error(e.response?.data?.error || 'Failed to add user');
      }
    } finally {
      setAdding(false);
      setAddAndSend(false);
    }
  };

  /** Stacked date/time — fits narrow columns without overlapping neighbors */
  const formatTableDate = (d) => {
    if (!d) return <span className="text-gray-400">—</span>;
    const x = new Date(d);
    return (
      <div className="leading-tight">
        <div className="whitespace-nowrap text-gray-800 dark:text-zinc-200">
          {x.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' })}
        </div>
        <div className="whitespace-nowrap text-xs tabular-nums text-gray-500 dark:text-zinc-500">
          {x.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })}
        </div>
      </div>
    );
  };

  const startEditName = (u) => {
    setEditingEmailFor(null);
    setEditingNameFor(u.id);
    setEditingNameValue(u.name || '');
  };

  const saveName = async (userId) => {
    const value = editingNameValue.trim();
    setSavingNameFor(userId);
    try {
      const res = await axios.patch(
        `/api/admin/crm/users/${userId}`,
        { name: value || null },
        { withCredentials: true }
      );
      if (res.data.success) {
        setUsers((prev) => prev.map((x) => (x.id === userId ? { ...x, name: res.data.user.name } : x)));
        toast.success('Name updated');
      } else {
        toast.error(res.data.error || 'Failed to update name');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || 'Failed to update name');
    } finally {
      setSavingNameFor(null);
      setEditingNameFor(null);
    }
  };

  const cancelEditName = () => {
    setEditingNameFor(null);
    setEditingNameValue('');
  };

  const startEditEmail = (u) => {
    setEditingEmailFor(u.id);
    setEditingEmailValue(u.email || '');
    setEditingNameFor(null);
  };

  const saveEmail = async (userId) => {
    const value = editingEmailValue.trim().toLowerCase();
    if (!value) {
      toast.error('Email is required');
      return;
    }
    setSavingEmailFor(userId);
    try {
      const res = await axios.patch(
        `/api/admin/crm/users/${userId}`,
        { email: value },
        { withCredentials: true }
      );
      if (res.data.success) {
        setUsers((prev) =>
          prev.map((x) => (x.id === userId ? { ...x, email: res.data.user.email } : x))
        );
        toast.success('Email updated');
      } else {
        toast.error(res.data.error || 'Failed to update email');
      }
    } catch (e) {
      toast.error(e.response?.data?.error || e.response?.data?.message || 'Failed to update email');
    } finally {
      setSavingEmailFor(null);
      setEditingEmailFor(null);
    }
  };

  const cancelEditEmail = () => {
    setEditingEmailFor(null);
    setEditingEmailValue('');
  };

  const handleSort = (column) => {
    if (sortBy === column) {
      setSortDir((d) => (d === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(column);
      setSortDir(
        column === 'created_at' ||
          column === 'last_login_at' ||
          column === 'brand_settings_updated_at' ||
          column === 'login_count' ||
          column === 'digest_sent' ||
          column === 'digest_opens'
          ? 'desc'
          : 'asc'
      );
    }
  };

  const digestByUser = digestMetrics?.byUser || {};

  const usersWithDigest = React.useMemo(
    () =>
      users.map((u) => ({
        ...u,
        digest_sent: digestByUser[String(u.id)]?.sent ?? 0,
        digest_opens: digestByUser[String(u.id)]?.opens ?? 0
      })),
    [users, digestByUser]
  );

  const sortedUsers = React.useMemo(() => {
    const key = sortBy;
    const dir = sortDir === 'asc' ? 1 : -1;
    return [...usersWithDigest].sort((a, b) => {
      let va = a[key];
      let vb = b[key];
      if (key === 'created_at' || key === 'last_login_at' || key === 'brand_settings_updated_at') {
        const ta = va ? new Date(va).getTime() : 0;
        const tb = vb ? new Date(vb).getTime() : 0;
        if (ta !== tb) return (ta - tb) * dir;
        return (a.id - b.id) * dir;
      }
      if (key === 'login_count' || key === 'digest_sent' || key === 'digest_opens') {
        const na = Number(va) || 0;
        const nb = Number(vb) || 0;
        if (na !== nb) return (na - nb) * dir;
        return (a.id - b.id) * dir;
      }
      const sa = (va == null || va === '' ? '' : String(va)).toLowerCase();
      const sb = (vb == null || vb === '' ? '' : String(vb)).toLowerCase();
      const cmp = sa.localeCompare(sb, undefined, { sensitivity: 'base' });
      if (cmp !== 0) return cmp * dir;
      return (a.id - b.id) * dir;
    });
  }, [usersWithDigest, sortBy, sortDir]);

  const SortableTh = ({ column, label, subLabel, icon: Icon, className = '', title: thTitle, align = 'left' }) => {
    const active = sortBy === column;
    const alignTh = align === 'center' ? 'text-center' : 'text-left';
    const btnAlign =
      align === 'center'
        ? 'justify-center w-full text-center'
        : 'text-left';
    return (
      <th
        scope="col"
        title={thTitle}
        className={`px-3 py-3 font-medium align-bottom ${alignTh} ${className}`}
      >
        <button
          type="button"
          onClick={() => handleSort(column)}
          className={`inline-flex ${align === 'center' ? 'flex-nowrap' : 'flex-wrap'} items-center gap-x-1 gap-y-0.5 hover:text-gray-800 dark:hover:text-zinc-100 cursor-pointer max-w-full ${btnAlign}`}
        >
          {Icon ? <Icon className="h-3.5 w-3.5 shrink-0 text-gray-400" /> : null}
          <span className="whitespace-nowrap">{label}</span>
          {subLabel != null && (
            <span className="font-normal text-gray-400 dark:text-zinc-500 text-xs whitespace-nowrap">{subLabel}</span>
          )}
          {active ? (
            sortDir === 'asc' ? (
              <ArrowUp className="h-3.5 w-3.5 shrink-0" />
            ) : (
              <ArrowDown className="h-3.5 w-3.5 shrink-0" />
            )
          ) : (
            <span className="w-3.5 h-3.5 shrink-0 inline-block opacity-25">↕</span>
          )}
        </button>
      </th>
    );
  };

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-950 py-8 px-4 sm:px-6 lg:px-8">
      <div className="max-w-[100rem] mx-auto">
        <div className="flex items-center justify-between mb-6">
          <div className="flex items-center gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="p-2 rounded-lg bg-white dark:bg-zinc-800 border border-gray-200 dark:border-zinc-700 hover:bg-gray-50 dark:hover:bg-zinc-700"
            >
              <ArrowLeft className="h-5 w-5 text-gray-600 dark:text-zinc-300" />
            </button>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
              <Users className="h-8 w-8 text-indigo-600" />
              CRM – Users
            </h1>
          </div>
        </div>

        {/* Subscriber stats for investors */}
        <div className="mb-6 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-emerald-100 dark:bg-emerald-900/30">
              <CreditCard className="h-6 w-6 text-emerald-600 dark:text-emerald-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-zinc-400">Total subscribers</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loadingStats ? '—' : (stats?.totalSubscribers ?? '—')}
              </p>
              <p className="text-xs text-gray-400 dark:text-zinc-500 mt-0.5">Digest subscribed</p>
            </div>
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-amber-100 dark:bg-amber-900/30">
              <DollarSign className="h-6 w-6 text-amber-600 dark:text-amber-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-zinc-400">Paying</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loadingStats ? '—' : (stats?.totalPaying ?? '—')}
              </p>
            </div>
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-indigo-100 dark:bg-indigo-900/30">
              <Users className="h-6 w-6 text-indigo-600 dark:text-indigo-400" />
            </div>
            <div>
              <p className="text-sm font-medium text-gray-500 dark:text-zinc-400">Total users</p>
              <p className="text-2xl font-bold text-gray-900 dark:text-white">
                {loadingStats ? '—' : (stats?.totalUsers ?? '—')}
              </p>
            </div>
          </div>
          <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-4 sm:col-span-2 lg:col-span-1">
            <p className="text-sm font-medium text-gray-500 dark:text-zinc-400 mb-2 flex items-center gap-1">
              <Calendar className="h-4 w-4" />
              New users by month
            </p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm">
              {loadingStats ? (
                <span className="text-gray-400">Loading…</span>
              ) : stats?.byMonth?.length ? (
                stats.byMonth.slice(0, 6).map((m) => (
                  <span key={m.month} className="text-gray-700 dark:text-zinc-300">
                    {m.label}: <strong>{m.newUsers}</strong>
                  </span>
                ))
              ) : (
                <span className="text-gray-400">No data</span>
              )}
            </div>
          </div>
        </div>
        {stats?.byWeek?.length > 0 && (
          <div className="mb-6 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-4">
            <p className="text-sm font-medium text-gray-500 dark:text-zinc-400 mb-2">New users by week (last 12 weeks, by signup date)</p>
            <div className="flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-700 dark:text-zinc-300">
              {stats.byWeek.slice(0, 6).map((w) => (
                <span key={w.week}>{w.label}: <strong>{w.newUsers}</strong>{w.subscribers != null && w.subscribers > 0 ? ` (${w.subscribers} subscribed)` : ''}</span>
              ))}
            </div>
          </div>
        )}

        {/* Trend Pro paywall waitlist */}
        <div className="mb-6 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Lock className="h-5 w-5 text-cyan-600 dark:text-cyan-400 shrink-0" />
                Trend Pro waitlist
              </h2>
              <p className="text-sm text-gray-600 dark:text-zinc-400 mt-1">
                Emails captured from the trend detail paywall (<span className="font-medium">Upgrade to unlock</span>).
                Same rows as <code className="text-xs bg-gray-100 dark:bg-zinc-800 px-1 rounded">t_trend_pro_chat_waitlist</code>.
              </p>
              {trendProWaitlistNote && (
                <p className="text-sm text-amber-700 dark:text-amber-400 mt-2">{trendProWaitlistNote}</p>
              )}
            </div>
            <div className="flex items-center gap-3 shrink-0">
              <p className="text-sm text-gray-600 dark:text-zinc-400">
                <span className="font-semibold text-gray-900 dark:text-white">{loadingTrendProWaitlist ? '—' : trendProWaitlistTotal}</span>
                {trendProWaitlistTotal !== trendProWaitlistReturned && trendProWaitlistReturned > 0 && (
                  <span className="text-gray-500 dark:text-zinc-500"> ({trendProWaitlistReturned} shown)</span>
                )}{' '}
                signups
              </p>
              <button
                type="button"
                onClick={() => fetchTrendProWaitlist(true)}
                disabled={refreshingTrendProWaitlist || loadingTrendProWaitlist}
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-medium rounded-lg border border-gray-300 dark:border-zinc-600 bg-gray-50 dark:bg-zinc-800 text-gray-800 dark:text-zinc-200 hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-50"
              >
                {refreshingTrendProWaitlist ? <Loader2 className="h-4 w-4 animate-spin" /> : <RefreshCw className="h-4 w-4" />}
                Refresh
              </button>
            </div>
          </div>
          <div className="overflow-x-auto rounded-lg border border-gray-200 dark:border-zinc-700">
            <table className="min-w-full text-sm">
              <thead className="bg-gray-50 dark:bg-zinc-800/80 text-left text-gray-600 dark:text-zinc-400">
                <tr>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Email</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">User ID</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Feature</th>
                  <th className="px-3 py-2 font-medium whitespace-nowrap">Joined</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 dark:divide-zinc-700 text-gray-900 dark:text-zinc-200">
                {loadingTrendProWaitlist ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-8 text-center text-gray-500 dark:text-zinc-500">
                      <Loader2 className="h-6 w-6 animate-spin inline-block mr-2 align-middle" />
                      Loading…
                    </td>
                  </tr>
                ) : trendProWaitlist.length === 0 ? (
                  <tr>
                    <td colSpan={4} className="px-3 py-6 text-center text-gray-500 dark:text-zinc-500">
                      No waitlist entries yet.
                    </td>
                  </tr>
                ) : (
                  trendProWaitlist.map((row) => (
                    <tr key={row.id} className="hover:bg-gray-50/80 dark:hover:bg-zinc-800/50">
                      <td className="px-3 py-2 font-mono text-xs sm:text-sm">{row.email}</td>
                      <td className="px-3 py-2 text-gray-600 dark:text-zinc-400 tabular-nums">
                        {row.user_id != null ? row.user_id : '—'}
                      </td>
                      <td className="px-3 py-2 text-gray-600 dark:text-zinc-400 font-mono text-xs">
                        {row.feature_key || '—'}
                      </td>
                      <td className="px-3 py-2 whitespace-nowrap">{formatTableDate(row.created_at)}</td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

        {/* Add user card */}
        <div className="mb-6 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-6">
          <h2 className="text-lg font-semibold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
            <UserPlus className="h-5 w-5" />
            Add user (new or existing)
          </h2>
          <div className="flex flex-wrap items-end gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Name</label>
              <input
                type="text"
                value={addName}
                onChange={(e) => setAddName(e.target.value)}
                placeholder="Optional"
                className="w-48 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-zinc-300 mb-1">Email</label>
              <input
                type="email"
                value={addEmail}
                onChange={(e) => setAddEmail(e.target.value)}
                placeholder="user@example.com"
                className="w-56 px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white"
              />
            </div>
            <div className="flex gap-2">
              <button
                onClick={() => handleAddUser(false)}
                disabled={adding}
                className="px-4 py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {adding && !addAndSend ? <Loader2 className="h-4 w-4 animate-spin" /> : <UserPlus className="h-4 w-4" />}
                Add user
              </button>
              <button
                onClick={() => { setAddAndSend(true); handleAddUser(true); }}
                disabled={adding}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50 flex items-center gap-2"
              >
                {adding && addAndSend ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                Add & send 7-day digest
              </button>
            </div>
          </div>
        </div>

        {/* Sunday digest — weekly sends vs opens (per selected period) */}
        <div className="mb-6 bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl p-4 sm:p-6">
          <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4">
            <div>
              <h2 className="text-lg font-semibold text-gray-900 dark:text-white flex items-center gap-2">
                <Calendar className="h-5 w-5 text-indigo-500 shrink-0" />
                Sunday weekly digest
              </h2>
              <p className="text-sm text-gray-600 dark:text-zinc-400 mt-1">
                Totals and per-user counts for the digest window you pick (aligned with the Sunday HTML email period).
              </p>
            </div>
            <div className="flex flex-col gap-1 min-w-[min(100%,18rem)]">
              <label className="text-xs font-medium text-gray-500 dark:text-zinc-400">Digest period</label>
              <select
                value={
                  selectedDigestPeriod
                    ? `${selectedDigestPeriod.periodStart}|${selectedDigestPeriod.periodEnd}`
                    : ''
                }
                onChange={(e) => {
                  const v = e.target.value;
                  const p = digestPeriods.find((x) => `${x.periodStart}|${x.periodEnd}` === v);
                  if (p) setSelectedDigestPeriod(p);
                }}
                disabled={!digestPeriods.length}
                className="px-3 py-2 border border-gray-300 dark:border-zinc-600 rounded-lg bg-white dark:bg-zinc-800 text-gray-900 dark:text-white text-sm disabled:opacity-50"
              >
                {digestPeriods.length === 0 ? (
                  <option value="">No periods logged yet</option>
                ) : (
                  digestPeriods.map((p) => (
                    <option key={`${p.periodStart}-${p.periodEnd}`} value={`${p.periodStart}|${p.periodEnd}`}>
                      {p.label || `${p.periodStart} – ${p.periodEnd}`}
                    </option>
                  ))
                )}
              </select>
            </div>
          </div>
          <div className="mt-4 grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4">
            <div className="rounded-lg border border-gray-100 dark:border-zinc-800 bg-gray-50/80 dark:bg-zinc-800/40 px-3 py-3">
              <p className="text-xs font-medium text-gray-500 dark:text-zinc-400">Total sends</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                {loadingDigestMetrics ? '…' : digestMetrics?.summary?.totalSends ?? '—'}
              </p>
              <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-1">Sum of send attempts for this period</p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-zinc-800 bg-gray-50/80 dark:bg-zinc-800/40 px-3 py-3">
              <p className="text-xs font-medium text-gray-500 dark:text-zinc-400">Unique openers</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                {loadingDigestMetrics ? '…' : digestMetrics?.summary?.uniqueOpeners ?? '—'}
              </p>
              <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-1">Distinct users (tracking pixel)</p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-zinc-800 bg-gray-50/80 dark:bg-zinc-800/40 px-3 py-3">
              <p className="text-xs font-medium text-gray-500 dark:text-zinc-400">Open rate</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                {loadingDigestMetrics
                  ? '…'
                  : digestMetrics?.summary?.openRate != null
                    ? `${Math.round(digestMetrics.summary.openRate * 1000) / 10}%`
                    : '—'}
              </p>
              <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-1">Unique openers ÷ total sends</p>
            </div>
            <div className="rounded-lg border border-gray-100 dark:border-zinc-800 bg-gray-50/80 dark:bg-zinc-800/40 px-3 py-3 col-span-2 lg:col-span-1">
              <p className="text-xs font-medium text-gray-500 dark:text-zinc-400">Pixel loads</p>
              <p className="text-xl font-bold text-gray-900 dark:text-white tabular-nums mt-0.5">
                {loadingDigestMetrics ? '…' : digestMetrics?.summary?.totalPixelLoads ?? '—'}
              </p>
              <p className="text-[11px] text-gray-400 dark:text-zinc-500 mt-1">All open.gif hits (can exceed uniques)</p>
            </div>
          </div>
        </div>

        {/* Users table */}
        <div className="bg-white dark:bg-zinc-900 border border-gray-200 dark:border-zinc-700 rounded-xl overflow-hidden">
          <div className="px-4 sm:px-6 py-4 border-b border-gray-200 dark:border-zinc-700">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-white">All users</h2>
            <p className="text-sm text-gray-600 dark:text-zinc-400 mt-1">
              Sunday digest subscription, weekly send/open counts for the selected period, one-off 7-day emails, Friday AI briefs, and engagement. Wider than the viewport? Scroll sideways.
            </p>
            <details className="mt-3 group rounded-lg border border-gray-200 dark:border-zinc-700 bg-gray-50/80 dark:bg-zinc-800/50 px-3 py-2 text-sm text-gray-600 dark:text-zinc-400">
              <summary className="flex cursor-pointer list-none items-center gap-2 font-medium text-gray-800 dark:text-zinc-200 [&::-webkit-details-marker]:hidden">
                <Info className="h-4 w-4 shrink-0 text-indigo-500" />
                What each column means
                <span className="text-xs font-normal text-gray-500 dark:text-zinc-500">(click to expand)</span>
              </summary>
              <ul className="mt-3 space-y-2 pl-6 list-disc text-xs sm:text-sm leading-relaxed">
                <li>
                  <strong className="text-gray-700 dark:text-zinc-300">Code logins</strong> — successful email verification sign-ins only (not every page load).
                </li>
                <li>
                  <strong className="text-gray-700 dark:text-zinc-300">Brand updated</strong> — last save time for brand settings. Compare with logins; the in-app reminder uses the client’s 3rd login (session), which is only loosely aligned with this count.
                </li>
                <li>
                  <strong className="text-gray-700 dark:text-zinc-300">Friday brief</strong> — enabled only when brand name and description are complete; test send saves line items for review but does not write the weekly send log row.
                </li>
                <li>
                  <strong className="text-gray-700 dark:text-zinc-300">Weekly sent / Weekly opens</strong> — counts for the digest period chosen above (Sunday HTML digest); opens reflect tracking pixel loads.
                </li>
              </ul>
            </details>
          </div>
          {loading ? (
            <div className="flex items-center justify-center py-16">
              <Loader2 className="h-8 w-8 animate-spin text-indigo-600" />
            </div>
          ) : (
            <div className="overflow-x-auto overscroll-x-contain">
              <table className="w-full min-w-[1500px] border-separate border-spacing-0 text-sm">
                <thead>
                  <tr className="bg-gray-50 dark:bg-zinc-800 text-left text-xs sm:text-sm text-gray-500 dark:text-zinc-400 border-b border-gray-200 dark:border-zinc-700">
                    <SortableTh column="name" label="Name" className="min-w-[8rem]" />
                    <SortableTh
                      column="email"
                      label="Email"
                      subLabel="(domain)"
                      className="min-w-[12rem]"
                      thTitle="Sorts by full address; useful for grouping by domain"
                    />
                    <SortableTh column="brand_name" label="Brand" icon={Building2} className="min-w-[13rem] max-w-[20rem]" />
                    <SortableTh column="created_at" label="Created" className="min-w-[6.5rem]" />
                    <SortableTh column="last_login_at" label="Last login" className="min-w-[6.5rem]" />
                    <SortableTh
                      column="login_count"
                      label="Logins"
                      icon={LogIn}
                      subLabel="code"
                      className="min-w-[4.5rem]"
                      thTitle="Email code verification logins"
                      align="center"
                    />
                    <SortableTh column="brand_settings_updated_at" label="Brand Δ" className="min-w-[6.5rem]" thTitle="Brand settings last updated" />
                    <th scope="col" className="px-3 py-3 font-medium align-bottom min-w-[9.5rem]">Sun digest</th>
                    <SortableTh
                      column="digest_sent"
                      label="Weekly sent"
                      className="min-w-[4.5rem]"
                      thTitle="Digest emails logged for the selected week (resends count)"
                      align="center"
                    />
                    <SortableTh
                      column="digest_opens"
                      label="Weekly opens"
                      className="min-w-[4.5rem]"
                      thTitle="Tracking pixel loads for that digest period"
                      align="center"
                    />
                    <th scope="col" className="px-3 py-3 font-medium align-bottom text-right min-w-[11rem] whitespace-nowrap">
                      7-day email
                    </th>
                    <th scope="col" className="px-3 py-3 font-medium align-bottom text-right min-w-[10.5rem] whitespace-nowrap">
                      <span className="inline-flex items-center justify-end gap-1 w-full">
                        <Sparkles className="h-3.5 w-3.5 shrink-0" />
                        Fri brief
                      </span>
                    </th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-200 dark:divide-zinc-700">
                  {users.length === 0 ? (
                    <tr>
                      <td colSpan={12} className="px-6 py-12 text-center text-gray-500 dark:text-zinc-400 border-t border-gray-100 dark:border-zinc-800/80">
                        No users yet.
                      </td>
                    </tr>
                  ) : (
                    sortedUsers.map((u) => (
                      <tr key={u.id} className="hover:bg-gray-50 dark:hover:bg-zinc-800/50 align-middle">
                        <td className="px-3 py-3 text-gray-900 dark:text-white align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          {editingNameFor === u.id ? (
                            <div className="flex items-center gap-2">
                              <input
                                type="text"
                                value={editingNameValue}
                                onChange={(e) => setEditingNameValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveName(u.id);
                                  if (e.key === 'Escape') cancelEditName();
                                }}
                                className="px-2 py-1 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-800 text-gray-900 dark:text-white text-sm w-40"
                                autoFocus
                              />
                              <button
                                type="button"
                                onClick={() => saveName(u.id)}
                                disabled={savingNameFor === u.id}
                                className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                title="Save"
                              >
                                {savingNameFor === u.id ? <Loader2 className="h-4 w-4 animate-spin" /> : <Check className="h-4 w-4" />}
                              </button>
                              <button
                                type="button"
                                onClick={cancelEditName}
                                className="p-1 text-gray-500 hover:bg-gray-100 dark:hover:bg-zinc-700 rounded text-sm"
                              >
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <span className="flex items-center gap-2">
                              {u.name || '—'}
                              <button
                                type="button"
                                onClick={() => startEditName(u)}
                                className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded"
                                title="Edit name"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-gray-700 dark:text-zinc-300 align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          {editingEmailFor === u.id ? (
                            <div className="flex flex-col gap-2">
                              <input
                                type="email"
                                value={editingEmailValue}
                                onChange={(e) => setEditingEmailValue(e.target.value)}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') saveEmail(u.id);
                                  if (e.key === 'Escape') cancelEditEmail();
                                }}
                                className="px-2 py-1 border border-gray-300 dark:border-zinc-600 rounded bg-white dark:bg-zinc-800 text-gray-900 dark:text-white text-sm w-full max-w-[14rem]"
                                autoFocus
                              />
                              <div className="flex items-center gap-2">
                                <button
                                  type="button"
                                  onClick={() => saveEmail(u.id)}
                                  disabled={savingEmailFor === u.id}
                                  className="p-1 text-green-600 dark:text-green-400 hover:bg-green-50 dark:hover:bg-green-900/20 rounded"
                                  title="Save email"
                                >
                                  {savingEmailFor === u.id ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                  ) : (
                                    <Check className="h-4 w-4" />
                                  )}
                                </button>
                                <button
                                  type="button"
                                  onClick={cancelEditEmail}
                                  className="text-xs text-gray-500 hover:text-gray-700 dark:hover:text-zinc-300"
                                >
                                  Cancel
                                </button>
                              </div>
                            </div>
                          ) : (
                            <span className="flex items-center gap-2 break-all text-sm">
                              <Mail className="h-4 w-4 text-gray-400 shrink-0" />
                              {u.email}
                              <button
                                type="button"
                                onClick={() => startEditEmail(u)}
                                className="p-1 text-gray-400 hover:text-indigo-600 dark:hover:text-indigo-400 rounded shrink-0"
                                title="Edit email"
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                            </span>
                          )}
                        </td>
                        <td className="px-3 py-3 align-top text-sm border-t border-gray-100 dark:border-zinc-800/80">
                          {u.brand_name || u.brand_description ? (
                            <div
                              className={`max-w-[18rem] ${u.brief_digest_eligible ? '' : 'opacity-50'}`}
                              title={
                                u.brief_digest_eligible
                                  ? undefined
                                  : 'Need active account, brand name & description, and Friday briefs enabled'
                              }
                            >
                              <div className="font-medium text-gray-900 dark:text-white line-clamp-1">
                                {u.brand_name || '—'}
                              </div>
                              <div className="text-gray-500 dark:text-zinc-400 line-clamp-2 mt-0.5 text-xs leading-snug">
                                {u.brand_description || '—'}
                              </div>
                              {(u.target_audience || u.content_themes) && (
                                <div className="text-gray-400 dark:text-zinc-500 text-xs mt-1 line-clamp-1">
                                  {u.target_audience ? `Audience: ${u.target_audience}` : ''}
                                  {u.target_audience && u.content_themes ? ' · ' : ''}
                                  {u.content_themes ? `Themes: ${u.content_themes}` : ''}
                                </div>
                              )}
                            </div>
                          ) : (
                            <span className="text-gray-400 dark:text-zinc-500 text-xs">No brand settings</span>
                          )}
                        </td>
                        <td className="px-3 py-3 text-gray-600 dark:text-zinc-400 align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          {formatTableDate(u.created_at)}
                        </td>
                        <td className="px-3 py-3 text-gray-600 dark:text-zinc-400 align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          {formatTableDate(u.last_login_at)}
                        </td>
                        <td className="px-3 py-3 text-gray-800 dark:text-zinc-200 text-sm tabular-nums text-center align-middle font-medium border-t border-gray-100 dark:border-zinc-800/80">
                          {u.login_count != null ? Number(u.login_count) : 0}
                        </td>
                        <td className="px-3 py-3 text-gray-600 dark:text-zinc-400 align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          {formatTableDate(u.brand_settings_updated_at)}
                        </td>
                        <td className="px-3 py-3 align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          <div className="flex flex-col gap-2 items-start">
                            {u.weekly_digest_subscribed ? (
                              <span className="text-green-600 dark:text-green-400 text-xs font-semibold uppercase tracking-wide">
                                Subscribed
                              </span>
                            ) : (
                              <span className="text-gray-500 dark:text-zinc-400 text-xs">Not subscribed</span>
                            )}
                            <button
                              type="button"
                              onClick={() => handleToggleSubscription(u.id, u.weekly_digest_subscribed)}
                              disabled={togglingSubscriptionFor !== null}
                              className="inline-flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md border border-gray-300 dark:border-zinc-600 hover:bg-gray-100 dark:hover:bg-zinc-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                            >
                              {togglingSubscriptionFor === u.id ? (
                                <Loader2 className="h-3 w-3 animate-spin" />
                              ) : null}
                              {u.weekly_digest_subscribed ? 'Unsubscribe' : 'Subscribe'}
                            </button>
                          </div>
                        </td>
                        <td className="px-3 py-3 text-center tabular-nums text-sm text-gray-800 dark:text-zinc-200 align-middle border-t border-gray-100 dark:border-zinc-800/80 font-medium">
                          {u.digest_sent}
                        </td>
                        <td className="px-3 py-3 text-center tabular-nums text-sm text-gray-800 dark:text-zinc-200 align-middle border-t border-gray-100 dark:border-zinc-800/80 font-medium">
                          {u.digest_opens}
                        </td>
                        <td className="px-3 py-3 text-right align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          <button
                            type="button"
                            onClick={() => handleSendDigest(u.email)}
                            disabled={sendingDigestFor !== null}
                            className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-green-600 text-white text-xs sm:text-sm rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap min-h-[2.25rem] w-full sm:w-auto sm:min-w-[10.5rem]"
                          >
                            {sendingDigestFor === u.email ? (
                              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                            ) : (
                              <Send className="h-4 w-4 shrink-0" />
                            )}
                            <span>Send 7-day digest</span>
                          </button>
                        </td>
                        <td className="px-3 py-3 text-right align-middle border-t border-gray-100 dark:border-zinc-800/80">
                          <button
                            type="button"
                            onClick={() => handleSendBriefDigest(u.email, !!u.brief_digest_eligible)}
                            disabled={!u.brief_digest_eligible || sendingBriefDigestFor !== null}
                            title={
                              u.brief_digest_eligible
                                ? 'Send Friday AI brief digest (test). Line items saved for internal review; weekly send log row is not created.'
                                : 'Requires active user, brand name & description, and Friday brief subscription'
                            }
                            className={`inline-flex items-center justify-center gap-2 px-3 py-2 text-xs sm:text-sm rounded-lg whitespace-nowrap min-h-[2.25rem] w-full sm:w-auto sm:min-w-[9.5rem] ${
                              u.brief_digest_eligible
                                ? 'bg-teal-600 text-white hover:bg-teal-700 disabled:opacity-50 disabled:cursor-not-allowed'
                                : 'bg-gray-200 text-gray-400 dark:bg-zinc-800 dark:text-zinc-500 cursor-not-allowed'
                            }`}
                          >
                            {sendingBriefDigestFor === u.email ? (
                              <Loader2 className="h-4 w-4 animate-spin shrink-0" />
                            ) : (
                              <Sparkles className="h-4 w-4 shrink-0" />
                            )}
                            <span>Send Fri brief</span>
                          </button>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminCRM;
